import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Modal, Button, ModalBody } from 'reactstrap';
import { FormattedMessage, injectIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faXmark } from '@fortawesome/free-solid-svg-icons';

import {
  requestTwoFactor as requestTwoFactorAction,
  enableTwoFactor as enableTwoFactorAction,
  disableTwoFactor as disableTwoFactorAction,
  cancelTwoFactor as cancelTwoFactorAction,
} from 'Redux/ducks/two-factor';

import 'Components/Account/LoginAndSecurity/TwoFactorAuthContainer.scss';

const TwoFactorAuthContainer = ({
  requestTwoFactor,
  enableTwoFactor,
  disableTwoFactor,
  cancelTwoFactor,
  twoFactorEnabled,
  twoFactor,
  emailConfirmed,
  accountReminder,
}) => {
  const [token, setToken] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPane, setModalPane] = useState(1);
  const [hideAccountBanner, setHideAccountBanner] = useState(false);

  useEffect(() => {
    if (accountReminder) {
      const dismissedAccountTwoFactor = localStorage.getItem('dismissedAccountTwoFactor');
      const twentyFourHours = 86400000;
      const oneMonth = twentyFourHours * 30;

      if (dismissedAccountTwoFactor && (Date.now() - dismissedAccountTwoFactor) < oneMonth) {
        setHideAccountBanner(true);
      }
    }
  }, [accountReminder]);

  const toggle = () => {
    setModalOpen(!modalOpen);
    setModalPane(1);
  };

  const request = () => {
    requestTwoFactor();
    setModalOpen(!modalOpen);
  };

  const enable = () => {
    enableTwoFactor(token);
  };

  const disable = () => {
    disableTwoFactor();
    setModalOpen(!modalOpen);
  };

  const cancel = () => {
    cancelTwoFactor();
    setModalOpen(!modalOpen);
    setModalPane(1);
  };

  const handleChange = (e) => {
    setToken(e.target.value);
  };

  const closeBanner = () => {
    localStorage.setItem('dismissedAccountTwoFactor', Date.now());
    setHideAccountBanner(true);
  };

  const enableButton = (location) => {
    if (!emailConfirmed) {
      return (
        <button className="account-table-action-button disabled">
          <FormattedMessage id="account.twoFactor.confirm.first" defaultMessage="Please confirm your account first" />
        </button>
      );
    }
    return (
      <button type="button" className={`${location.pathname === '/account/login' ? 'two-factor-simple-btn' : 'btn btn-primary two-factor-action-button'}`} onClick={() => request()}>
        <strong>
          <FormattedMessage id="account.twoFactor.enable" defaultMessage="Enable 2FA" />
        </strong>
      </button>
    );
  };

  if (accountReminder && (twoFactorEnabled || hideAccountBanner)) return false;

  return (
    <React.Fragment>
      {!accountReminder &&
        <div className="account-table-row">
          <div className="account-table-row-title">
            <FormattedMessage id="account.twoFactor" defaultMessage="Two-Factor Authentication (2FA)" />
          </div>
          <div className="account-table-row-content">
            <div className="account-table-details-container">
              {twoFactorEnabled &&
                <span className="highlighted-text">
                  <FormattedMessage id="account.twoFactor.enabled" defaultMessage="Enabled" />
                </span>
              }
              {!twoFactorEnabled &&
                <FormattedMessage id="account.twoFactor.disabled" defaultMessage="Disabled" />
              }
              <div className="account-table-details-smallprint">
                <FormattedMessage id="account.twoFactor.explanation" defaultMessage="Add an extra layer of security to your account." />&nbsp;
                <a href="https://support.fanatical.com/hc/en-us/articles/360002761397-" target="_blank" rel="noopener noreferrer">
                  <FormattedMessage id="account.twoFactor.explanation.link" defaultMessage="Find out more." />
                </a>
              </div>
            </div>
            <div className="account-table-action-container">
              {twoFactorEnabled && (!twoFactor || !twoFactor.qr) &&
                <button type="button" className={`${location.pathname === '/account/login' ? 'two-factor-simple-btn' : 'btn btn-primary two-factor-action-button'}`} onClick={() => toggle()}>
                  <strong>
                    <FormattedMessage id="account.twoFactor.disable" defaultMessage="Disable 2FA" />
                  </strong>
                </button>
              }
              {!twoFactorEnabled && (!twoFactor || !twoFactor.qr) &&
                <React.Fragment>
                  {enableButton(location)}
                </React.Fragment>
              }
            </div>
          </div>
        </div>
      }
      {accountReminder &&
        <article className="account-table">
          <h2 className="account-table-heading includes-button close-button">
            <div className="title-container">
              <FormattedMessage id="account.twoFactor.reminder.table.title" defaultMessage="Secure Your Account" />
            </div>
            <div className="button-container">
              <button className="btn" onClick={() => closeBanner()}>
                <FontAwesomeIcon icon={faXmark} title="Dismiss banner" />
              </button>
            </div>
          </h2>
          <div className="account-table-row">
            <div className="account-table-row-content">
              <div className="account-table-details-container">
                <FormattedMessage
                  id="account.twoFactor.reminder.table.text"
                  defaultMessage="Add an extra layer of security to your account by enabling Two-Factor Authentication (2FA). Once enabled, we'll ask for a verification code via your security method if we notice a login from an unrecognised device or browser."
                />
              </div>
              <div className="account-table-action-container">
                <React.Fragment>
                  {enableButton(location)}
                </React.Fragment>
              </div>
            </div>
          </div>
        </article>
      }

      <Modal isOpen={modalOpen} toggle={() => cancel()} className="account-modal">
        {!twoFactorEnabled && twoFactor && twoFactor.qr &&
          <ModalBody>
            <div className="account-modal-title">
              <FormattedMessage id="account.twoFactor.confim-title" defaultMessage="Confirm Two-Factor Authentication" />
            </div>
            {modalPane === 1 &&
              <div className="account-modal-form">
                <p className="account-modal-paragraph mb-0">
                  <FormattedMessage id="account.twoFactor.scan" defaultMessage="Please use your authentication app to scan this barcode:" />
                </p>
                <p className="account-modal-paragraph mb-0">
                  <a href="https://support.fanatical.com/hc/en-us/articles/360002761397-" target="_blank" rel="noopener noreferrer" className="account-modal-link">
                    <FormattedMessage id="account.twoFactor.help" defaultMessage="What is Two-Factor Authentication?" />
                  </a>
                </p>

                <div className="account-two-factor-qr-container">
                  <img src={twoFactor.qr} alt={twoFactor.url} />
                </div>

                <p className="account-modal-paragraph">
                  <FormattedMessage id="account.twoFactor.alternative" defaultMessage="Or enter this code into your authenticator app:" />
                </p>
                <input readOnly className="account-two-factor-code w-100" value={twoFactor.token} type="text" />
                <div className="account-modal-form-buttons">
                  <div className="form-button-container">
                    <Button color="primary" onClick={() => setModalPane(2)}>
                      <FormattedMessage id="account.twoFactor.button.next" defaultMessage="Next" />
                    </Button>
                  </div>
                </div>
              </div>
            }
            {modalPane === 2 &&
              <div className="account-modal-form">
                <p className="account-modal-paragraph">
                  <FormattedMessage id="account.twoFactor.verify" defaultMessage="Please enter the confirmation code that you see on your authentication app:" />
                </p>

                <div className="account-modal-form-input">
                  <div className="form-group">
                    <label htmlFor="token" className="account-modal-form-title">
                      <FormattedMessage id="account-two-factor.form.code" defaultMessage="Your Two-Factor Authentication Code:" />
                    </label>
                    <div className="account-modal-form-input">
                      <input
                        id="token"
                        aria-label="2FA Code"
                        type="text"
                        name="token"
                        maxLength="6"
                        value={token}
                        inputMode="decimal"
                        pattern="[0-9]{6}"
                        autoComplete="one-time-code"
                        className={classNames('form-control', { 'input-error': twoFactor.error })}
                        onChange={handleChange}
                      />
                      {twoFactor && twoFactor.error &&
                        <div className="invalid-field">
                          {twoFactor.error}
                        </div>
                      }
                    </div>
                  </div>
                </div>

                <div className="account-modal-form-buttons">
                  <div className="form-button-container">
                    <Button color="primary" onClick={() => enable()}>
                      <FormattedMessage id="account.2faconfirm" defaultMessage="Confirm and Enable" />
                    </Button>
                  </div>
                  <div className="form-button-container">
                    <Button color="secondary" onClick={() => setModalPane(1)}>
                      <FormattedMessage id="account.twoFactor.back" defaultMessage="Back" />
                    </Button>
                  </div>
                </div>
              </div>
            }
          </ModalBody>
        }

        {twoFactorEnabled && modalPane === 2 &&
          <ModalBody>
            <div className="account-modal-title">
              <FormattedMessage id="account.twoFactor.confim-title" defaultMessage="Confirm Two-Factor Authentication" />
            </div>
            <div className="account-modal-form">
              <div className="success-block">
                <FontAwesomeIcon icon={faCircleCheck} size="lg" />
                <FormattedMessage id="account.twoFactor.success" defaultMessage="Success" />
              </div>
              <p className="account-modal-paragraph mb-0">
                <FormattedMessage id="account.twoFactor.success-message" defaultMessage="Two-Factor Authentication is on. We will now ask for a login code whenever you log in." />
              </p>
              <div className="account-modal-form-buttons">
                <div className="form-button-container">
                  <Button color="primary" onClick={() => toggle()}>
                    <FormattedMessage id="account.twoFactor.close" defaultMessage="Close" />
                  </Button>
                </div>
              </div>
            </div>
          </ModalBody>
        }

        {twoFactorEnabled && modalPane === 1 &&
          <ModalBody>
            <div className="account-modal-title">
              <FormattedMessage id="account.twoFactor.disable-title" defaultMessage="Disable Two-Factor Authentication" />
            </div>
            <div className="account-modal-form">
              <p className="account-modal-paragraph">
                <FormattedMessage id="account.twoFactor.disable-message-one" defaultMessage="By disabling Two-Factor Authentication, codes will no longer be required to sign in to your account." />
              </p>
              <p className="account-modal-paragraph mb-0">
                <FormattedMessage id="account.twoFactor.disable-message-two" defaultMessage="This is not recommended because it reduces your account's security." />
              </p>

              <div className="account-modal-form-buttons">
                <div className="form-button-container">
                  <Button color="secondary" onClick={() => toggle()}>
                    <FormattedMessage id="account.twoFactor.button.cancel" defaultMessage="Cancel" />
                  </Button>
                </div>
                <div className="form-button-container">
                  <Button color="primary" onClick={() => disable()}>
                    <FormattedMessage id="account.twoFactor.button.disable" defaultMessage="Disable" />
                  </Button>
                </div>
              </div>
            </div>
          </ModalBody>
        }

        <div aria-label="close" className="close-button-container">
          <button aria-label="close" onClick={() => cancel()}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>
    </React.Fragment >
  );
};

TwoFactorAuthContainer.propTypes = {
  requestTwoFactor: PropTypes.func.isRequired,
  enableTwoFactor: PropTypes.func.isRequired,
  disableTwoFactor: PropTypes.func.isRequired,
  cancelTwoFactor: PropTypes.func.isRequired,
  twoFactorEnabled: PropTypes.bool,
  twoFactor: PropTypes.shape({}),
  accountReminder: PropTypes.bool,
};

TwoFactorAuthContainer.defaultProps = {
  twoFactorEnabled: false,
  twoFactor: null,
  accountReminder: false,
};

const mapStateToProps = ({
  router: { location },
  auth: { twoFactorEnabled, email_confirmed: emailConfirmed },
  twoFactor,
}) => ({
  twoFactor,
  twoFactorEnabled,
  emailConfirmed,
  location,
});

const mapDispatchToProps = {
  requestTwoFactor: requestTwoFactorAction,
  enableTwoFactor: enableTwoFactorAction,
  disableTwoFactor: disableTwoFactorAction,
  cancelTwoFactor: cancelTwoFactorAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(TwoFactorAuthContainer));
